import { inject, Injectable } from '@angular/core';
import { finalize, Observable, take } from 'rxjs';
import { OneMapAddress, OneMapApiService } from '@core/one-map';
import { LoadingService } from '@portal/loading/data-access';
import { PortalFieldConfig } from '@portal/form/data-access';
import { AddressModelKeys, BusinessEntityModelKeys } from '../const/form-field-key.const';
import { COMMON_VALIDATION, ENTITY_NAME } from '@core/data-access';

@Injectable({ providedIn: 'root' })
export class BusinessEntityAddressFormConfigService {
  private readonly oneMapApi = inject(OneMapApiService);
  private readonly loadingService = inject(LoadingService);

  initRegisteredAddressFormFields(isDisabled: boolean = false, hide: boolean = false, formState: any) {
    return {
      key: BusinessEntityModelKeys.address,
      fieldGroup: this.localAddressFields(isDisabled, true),
      expressions: { hide: () => hide },
    };
  }

  // initMailingAddressFormFields(isDisabled: boolean = false): PortalFieldConfig {
  //   return {
  //     className: 'sm:col-span-2',
  //     fieldGroup: [
  //       {
  //         className: 'col-start-1',
  //         key: BusinessEntityModelKeys.differentResidential,
  //         type: 'checkbox-boolean',
  //         defaultValue: false,
  //         props: {
  //           label: ADDRESS_TYPE.MAILING_ADDRESS,
  //           labelCheckbox: 'My Mailing Address is different from Residential Address',
  //           disabled: isDisabled,
  //         },
  //         hooks: {
  //           onInit: field => {
  //             const control = field.formControl;
  //             if (!control) return;
  //             control.valueChanges.subscribe(value => {
  //               field.model.mailingAddress = value === true ? AddressType.MAILING_ADDRESS : undefined;
  //             });
  //           },
  //         },
  //       },
  //       {
  //         key: BusinessEntityModelKeys.mailingAddress,
  //         className: 'sm:col-span-2',
  //         resetOnHide: true,
  //         expressions: { hide: model => !model?.parent?.model?.[PersonnelAddressModelKeys.differentResidential] },
  //         fieldGroupClassName: 'field-columns',
  //         fieldGroup: this.localAddressFields(isDisabled),
  //       },
  //     ],
  //   };
  // }

  private localAddressFields(isDisabled: boolean, noClass: boolean = false): PortalFieldConfig[] {
    return [
      {
        key: AddressModelKeys.postalCode,
        className: noClass ? '' : 'col-start-1',
        type: 'input',
        props: {
          label: 'Postal Code',
          maskConfig: { mask: '999999' },
          placeholder: 'e.g. 57212',
          required: true,
          minLength: 3,
          disabled: isDisabled,
        },
      },
      {
        className: noClass ? '' : 'col-start-1',
        type: 'single-button',
        props: {
          type: 'get-address',
          label: 'Get Address',
          disabled: isDisabled,
          click: ({ model, props, form }) => {
            const postal = model?.[AddressModelKeys.postalCode];
            if (!postal) return;

            props && (props.disabled = true);

            this.loadingService.show();
            this.getOneMapAddress(postal)
              .pipe(
                take(1),
                finalize(() => this.loadingService.hide()),
              )
              .subscribe(address => {
                props && (props.disabled = false);
                // console.log({ address });

                form?.get(AddressModelKeys.postalCode)?.setValue(address?.postalCode);
                form?.get(AddressModelKeys.blockNumber)?.setValue(address?.blockNumber);
                form?.get(AddressModelKeys.streetName)?.setValue(address?.streetName);
              });
          },
        },
      },
      {
        key: AddressModelKeys.blockNumber,
        className: noClass ? '' : 'col-start-1',
        type: 'input',
        props: {
          label: 'Block/House Number',
          maskConfig: { mask: 'A*' },
          placeholder: 'e.g. 371A',
          maxLength: 8,
          required: true,
          disabled: isDisabled,
        },
      },
      {
        key: AddressModelKeys.streetName,
        type: 'input',
        props: {
          label: 'Street Name',
          placeholder: 'e.g. Cavan Road',
          maxLength: 255,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: ENTITY_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
          required: true,
          disabled: isDisabled,
        },
      },
      {
        key: AddressModelKeys.storey,
        type: 'input',
        props: {
          label: 'Storey',
          placeholder: 'e.g. 7',
          maskConfig: { mask: '0*' },
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          disabled: isDisabled,
        },
      },
      {
        key: AddressModelKeys.unitNumber,
        type: 'input',
        props: {
          label: 'Unit Number',
          placeholder: 'e.g. 12',
          maskConfig: { mask: 'A*' },
          maxLength: 6,
          disabled: isDisabled,
        },
      },
      {
        key: AddressModelKeys.buildingName,
        type: 'input',
        props: {
          label: 'Building Name',
          placeholder: 'e.g. Casa del Rio',
          maxLength: 255,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: ENTITY_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
          disabled: isDisabled,
        },
      },
    ];
  }

  private getOneMapAddress(postal: string): Observable<OneMapAddress | null> {
    return this.oneMapApi.get(postal);
  }
}
